import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { DollarSign, TrendingUp, AlertCircle, CheckCircle } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const revenueData = [
  { month: 'Jan', revenue: 32400 },
  { month: 'Feb', revenue: 35100 },
  { month: 'Mar', revenue: 37800 },
  { month: 'Apr', revenue: 40200 },
  { month: 'May', revenue: 42500 },
  { month: 'Jun', revenue: 45600 },
  { month: 'Jul', revenue: 46800 },
  { month: 'Aug', revenue: 48320 },
];

const subscriptions = [
  {
    client: 'ABC Trading LLC',
    plan: 'Premium',
    amount: 499,
    billingCycle: 'Monthly',
    nextBilling: '2025-11-24',
    status: 'active',
    autoRenew: true,
  },
  {
    client: 'XYZ Corporation',
    plan: 'Standard',
    amount: 299,
    billingCycle: 'Monthly',
    nextBilling: '2025-11-15',
    status: 'active',
    autoRenew: true,
  },
  {
    client: 'Global Enterprises',
    plan: 'Basic',
    amount: 149,
    billingCycle: 'Monthly',
    nextBilling: '2025-09-30',
    status: 'expiring',
    autoRenew: false,
  },
  {
    client: 'Tech Solutions FZ',
    plan: 'Premium',
    amount: 499,
    billingCycle: 'Annually',
    nextBilling: '2026-03-20',
    status: 'active',
    autoRenew: true,
  },
  {
    client: 'Innovation Labs',
    plan: 'Standard',
    amount: 299,
    billingCycle: 'Monthly',
    nextBilling: '2025-11-05',
    status: 'pending',
    autoRenew: true,
  },
];

const plans = [
  {
    name: 'Basic',
    price: 149,
    features: ['Up to 1,000 invoices/month', 'Basic ERP integration', 'Email support', 'Standard reporting'],
    clients: 45,
  },
  {
    name: 'Standard',
    price: 299,
    features: ['Up to 5,000 invoices/month', 'All ERP integrations', 'Priority support', 'Advanced reporting'],
    clients: 62,
  },
  {
    name: 'Premium',
    price: 499,
    features: ['Unlimited invoices', 'Custom integrations', '24/7 support', 'Custom reporting', 'API access'],
    clients: 40,
  },
];

export default function SubscriptionBilling() {
  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl text-gray-900">Subscription & Billing</h1>
        <p className="text-gray-600 mt-1">Manage subscriptions and track revenue</p>
      </div>

      {/* Revenue Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Monthly Revenue</p>
                <p className="text-2xl text-gray-900 mt-2">$48,320</p>
                <div className="flex items-center mt-2 text-green-600">
                  <TrendingUp className="w-4 h-4 mr-1" />
                  <span className="text-sm">+23%</span>
                </div>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active Subscriptions</p>
                <p className="text-2xl text-gray-900 mt-2">132</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-[#0052CC]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Expiring Soon</p>
                <p className="text-2xl text-gray-900 mt-2">8</p>
              </div>
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Annual Revenue</p>
                <p className="text-2xl text-gray-900 mt-2">$512K</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Revenue Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Monthly Revenue Trend</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={revenueData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="revenue" fill="#0052CC" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Subscription Plans */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {plans.map((plan) => (
          <Card key={plan.name}>
            <CardHeader>
              <CardTitle>{plan.name}</CardTitle>
              <div className="mt-2">
                <span className="text-3xl text-gray-900">${plan.price}</span>
                <span className="text-gray-600">/month</span>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm text-gray-600 mb-2">Features:</p>
                <ul className="space-y-2">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="text-sm text-gray-700 flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="pt-4 border-t">
                <p className="text-sm text-gray-600">Active Clients: <span className="text-gray-900">{plan.clients}</span></p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Active Subscriptions Table */}
      <Card>
        <CardHeader>
          <CardTitle>Active Subscriptions</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Client</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Billing Cycle</TableHead>
                <TableHead>Next Billing</TableHead>
                <TableHead>Auto Renew</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {subscriptions.map((sub, index) => (
                <TableRow key={index}>
                  <TableCell>{sub.client}</TableCell>
                  <TableCell>
                    <Badge variant={sub.plan === 'Premium' ? 'default' : 'secondary'}>
                      {sub.plan}
                    </Badge>
                  </TableCell>
                  <TableCell>${sub.amount}</TableCell>
                  <TableCell>{sub.billingCycle}</TableCell>
                  <TableCell>{sub.nextBilling}</TableCell>
                  <TableCell>
                    {sub.autoRenew ? (
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    ) : (
                      <AlertCircle className="w-4 h-4 text-yellow-500" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        sub.status === 'active' ? 'default' :
                        sub.status === 'expiring' ? 'secondary' :
                        'outline'
                      }
                      className={
                        sub.status === 'active' ? 'bg-green-500' :
                        sub.status === 'expiring' ? 'bg-yellow-500' :
                        'bg-blue-500'
                      }
                    >
                      {sub.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="outline" size="sm">
                      View Details
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
